import { sortHand } from './tiles.js';
import { HUMAN_INDEX } from './constants.js';
import { aiCharlestonPick } from './ai.js';

// Offset per charleston step — number of seats to rotate outgoing tiles by,
// with seats arranged in turn order (East=0, South=1, West=2, North=3).
// Step 0: right (E→S), 1: across (E→W), 2: left (E→N), 3: left, 4: across,
// 5: right. "Right" = the player whose turn comes after yours.
export const CHARLESTON_OFFSETS = [1, 2, 3, 3, 2, 1];
export const SEAT_ORDER = ['East', 'South', 'West', 'North'];

// Simulate one charleston step. The human picks 3 tile ids up front; the AIs
// auto-pick. This mirrors the original monolith's simplified rotation: each
// AI seat hands its worst 3 to the next seat in index order, and the tail of
// the chain comes back to the human. Not a faithful right/across/left rotation
// yet — see DECISIONS.md.
export function applyCharlestonPass(state, humanPickedIds) {
  if (state.phase !== 'charleston') throw new Error('not_in_charleston');
  if (humanPickedIds.length !== 3) throw new Error('need_three_tiles');

  const humanHand = state.players[HUMAN_INDEX].hand;
  const idxs = humanPickedIds
    .map((id) => humanHand.findIndex((t) => t.id === id))
    .sort((a, b) => b - a);
  if (idxs.some((i) => i < 0)) throw new Error('tile_not_in_hand');

  let passed = idxs.map((i) => humanHand.splice(i, 1)[0]);

  for (let ai = 1; ai <= 3; ai++) {
    const aiPassed = aiCharlestonPick(state.players[ai]);
    for (const t of aiPassed) {
      const idx = state.players[ai].hand.findIndex((x) => x.id === t.id);
      if (idx >= 0) state.players[ai].hand.splice(idx, 1);
    }
    for (const t of passed.splice(0, Math.min(3, passed.length))) state.players[ai].hand.push(t);
    passed = aiPassed;
  }
  for (const t of passed) state.players[HUMAN_INDEX].hand.push(t);

  sortHand(state.players[HUMAN_INDEX].hand);
  state.charStep += 1;
  return state;
}

// Advance charleston to the play phase once all 6 steps are done.
// Returns true if the charleston is complete and the game moved to play.
export function maybeEndCharleston(state) {
  if (state.phase !== 'charleston' || state.charStep < 6) return false;
  state.phase = 'play';
  state.currentPlayer = 1; // East discards first; East already has 14 tiles.
  state.hasDrawn = true;
  return true;
}

// Apply one charleston round for online / 4-seat games. `passes` is an
// object keyed by seat name: { East: [id, id, id], South: [...], ... }.
// Each seat's three listed tile ids are removed from their hand and handed
// to the seat at offset CHARLESTON_OFFSETS[charStep] ahead of them in
// turn order. charStep is incremented. Throws on malformed input.
export function applyCharlestonRound(state, passes, dealerIndex = 0) {
  if (state.phase !== 'charleston') throw new Error('not_in_charleston');
  if (state.charStep < 0 || state.charStep >= CHARLESTON_OFFSETS.length) {
    throw new Error('bad_charleston_step');
  }
  const offset = CHARLESTON_OFFSETS[state.charStep];
  const outgoing = {};
  for (let i = 0; i < state.players.length; i++) {
    const seat = state.players[i].seat;
    const ids = passes[seat];
    if (!Array.isArray(ids) || ids.length !== 3) throw new Error('need_three_tiles_' + seat);
    const hand = state.players[i].hand;
    const grabbed = [];
    for (const id of ids) {
      const idx = hand.findIndex((t) => t.id === id);
      if (idx < 0) throw new Error('tile_not_in_hand_' + seat);
      grabbed.push(hand.splice(idx, 1)[0]);
    }
    outgoing[i] = grabbed;
  }
  for (let i = 0; i < state.players.length; i++) {
    const dest = (i + offset) % state.players.length;
    for (const t of outgoing[i]) state.players[dest].hand.push(t);
  }
  state.charStep += 1;
  return state;
}

// After all 6 rounds, transition state to play. dealerIndex is the engine
// index of the dealer — they already hold 14 tiles (from the deal) and
// start with hasDrawn=true.
export function finishCharleston(state, dealerIndex = 0) {
  state.phase = 'play';
  state.currentPlayer = dealerIndex;
  state.hasDrawn = true;
  state.charlestonPending = null;
  state.charPhase = null;
  state.charlestonVotes = null;
  state.courtesyPending = null;
  return state;
}

// Skip the optional second charleston (NMJL: it happens only if all four
// players agree — any one player may stop it after the first three passes).
export function skipSecondCharleston(state) {
  if (state.phase !== 'charleston') throw new Error('not_in_charleston');
  if (state.charStep !== 3) throw new Error('not_at_decision_point');
  state.charStep = 6;
  return state;
}

// NMJL courtesy pass: after the charleston, players facing each other may
// exchange 0–3 tiles; the pair exchanges the LESSER of the two offers (the
// player who offered more keeps their extras). `offers` is keyed by seat
// name: { East: [ids], South: [ids], West: [ids], North: [ids] } with 0–3
// tile ids each. Pairs are across the table: East↔West, South↔North.
export function applyCourtesyRound(state, offers) {
  if (state.phase !== 'charleston') throw new Error('not_in_charleston');
  const bySeat = {};
  state.players.forEach((p, i) => (bySeat[p.seat] = i));
  const pairs = [
    ['East', 'West'],
    ['South', 'North'],
  ];
  const exchanged = {};
  for (const [a, b] of pairs) {
    const ia = bySeat[a];
    const ib = bySeat[b];
    const offerA = Array.isArray(offers[a]) ? offers[a] : [];
    const offerB = Array.isArray(offers[b]) ? offers[b] : [];
    if (offerA.length > 3 || offerB.length > 3) throw new Error('too_many_tiles');
    const n = Math.min(offerA.length, offerB.length);
    exchanged[a] = n;
    exchanged[b] = n;
    if (n === 0) continue;
    const pull = (playerIdx, ids) => {
      const hand = state.players[playerIdx].hand;
      const grabbed = [];
      for (const id of ids.slice(0, n)) {
        const idx = hand.findIndex((t) => t.id === id);
        if (idx < 0) throw new Error('tile_not_in_hand_' + state.players[playerIdx].seat);
        grabbed.push(hand.splice(idx, 1)[0]);
      }
      return grabbed;
    };
    const fromA = pull(ia, offerA);
    const fromB = pull(ib, offerB);
    for (const t of fromA) state.players[ib].hand.push(t);
    for (const t of fromB) state.players[ia].hand.push(t);
  }
  if (state.players[HUMAN_INDEX]) sortHand(state.players[HUMAN_INDEX].hand);
  return exchanged; // { seat: tilesActuallyExchanged } for commentary
}
